import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Layout,
  Typography,
  Card,
  Button,
  Spin,
  Alert,
  Modal,
  Form,
  Input
} from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";

import ImageCardDisplay from "../../components/common/ImageCardDisplay";
import { getUserType } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import EventHeader from "../../components/Events/EventHeader";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import {
  submitEnquiry,
  resetSubmitEnquiryStatus,
  fetchEventBySlug,
  clearPublicEventBySlug,
  selectPublicEventBySlug,
  selectFetchEventBySlugStatus,
  selectFetchEventBySlugError,
} from "../../redux/eventSlice";
import DOMPurify from "dompurify";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function EventDetailsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const [event, setEvent] = useState(state?.event || null);

  const publicEventBySlug = useSelector(selectPublicEventBySlug);
  const slugFetchStatus = useSelector(selectFetchEventBySlugStatus);
  const slugFetchError = useSelector(selectFetchEventBySlugError);
  const enquirySuccess = useSelector((reduxState) => reduxState.event?.submitEnquirySuccess);
  const enquiryError = useSelector((reduxState) => reduxState.event?.submitEnquiryError);

  const activeRole = getUserType();
  const loadingStatus = event ? "completed" : slugFetchStatus === "loading" ? "loading" : slugFetchStatus;

  useEffect(() => {
    if (state?.event) {
      setEvent(state.event);
      return undefined;
    }

    if (!slug) return undefined;

    dispatch(clearPublicEventBySlug());
    dispatch(fetchEventBySlug(slug));

    return () => {
      dispatch(clearPublicEventBySlug());
    };
  }, [dispatch, slug, state?.event]);

  useEffect(() => {
    if (!state?.event && publicEventBySlug) {
      setEvent(publicEventBySlug);
    }
  }, [publicEventBySlug, state?.event]);

  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [enquiryForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const openEnquiryModal = () => setIsEnquiryModalOpen(true);
  const closeEnquiryModal = () => {
    setIsEnquiryModalOpen(false);
    enquiryForm.resetFields();
  };
  const handleEnquirySubmit = async (values) => {
    const payload = {
      enquiry_id: Math.floor(100 + Math.random() * 900),
      enquiry_name: values.enquiry_name,
      enquiry_email: values.enquiry_email,
      enquiry_contact: values.enquiry_contact
    };

    dispatch(submitEnquiry(payload));
  };

  useEffect(() => {
    if (enquirySuccess) {
      modal.info({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 22 }} />
            <div>Your enquiry was submitted successfully. </div>
          </div>
        ),
      });
      setIsEnquiryModalOpen(false);
      enquiryForm.resetFields();
      dispatch(resetSubmitEnquiryStatus());
    }
    if (enquiryError) {
      modal.info({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 22 }} />
            <div>Your enquiry was submitted failed. Please try again.</div>
          </div>
        ),
      });
      setIsEnquiryModalOpen(false);
      enquiryForm.resetFields();
      dispatch(resetSubmitEnquiryStatus());
    }
  }, [enquirySuccess, enquiryError, dispatch, enquiryForm, modal]);

  const eventSlug = event?.slug || slug;

  const handleBookEvent = () => {
    if (activeRole === "user") {
      navigate(`/event-details/booking/${eventSlug}`, { state: { event } });
    } else {
      navigate(`/login`, { state: { from: `/event-details/${eventSlug}` } });
    }
  };

  const parsePrice = (price) => {
    if (price === undefined || price === null) return 0;
    const match = String(price).match(/(\d+(\.\d+)?)/);
    return match ? Number(match[0]) : 0;
  };

  const description = event?.event_description || "No description available.";
  const descriptionHtml = (() => {
    const raw = String(description ?? "");
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    const normalized = looksLikeHtml ? raw : raw.replace(/\n/g, "<br/>");
    return DOMPurify.sanitize(normalized, {
      USE_PROFILES: { html: true },
    });
  })();

  let priceRange = "Price not available";
  if (event?.event_type_id === 1) {
    if (event?.event_allow_registration === false) {
      priceRange = "Free / No Registration";
    }
  } else if (event?.tickets && event?.tickets.length > 0) {
    const prices = event?.tickets.map(t => parsePrice(t.ticket_price || t.price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      priceRange = `₹${minPrice}`;
    } else {
      priceRange = `₹${minPrice} - ₹${maxPrice}`;
    }
  }

  if (loadingStatus === "loading") {
    return (
      <>
        <Header isHome={false}/>
        <Content className="topheaderLayout" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" tip="Loading event details..." />
        </Content>
        <Footer />
      </>
    );
  }

  if (!event && (slugFetchStatus === "failed" || slugFetchStatus === "completed")) {
    return (
      <>
        <Header isHome={false}/>
        <Content style={{ padding: '24px' }} className="topheaderLayout">
          <Alert
            message={slugFetchError ? "Failed to load event" : "Event not found"}
            type="warning"
            showIcon
          />
        </Content>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai"
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability,
          and city-specific visibility for organizers and users."
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata,
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai,
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai,
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      {contextHolder}

      <Modal
        title="Enquiry"
        open={isEnquiryModalOpen}
        onCancel={closeEnquiryModal}
        okText="Submit"
        okButtonProps={{ className: "btnGradient" }}
        cancelButtonProps={{ className: "draftBtn" }}
        onOk={() => enquiryForm.submit()}
        onClose={closeEnquiryModal}
      >
        <Form
          form={enquiryForm}
          layout="vertical"
          onFinish={handleEnquirySubmit}
        >
          <Form.Item
            label="Name"
            name="enquiry_name"
            rules={[{ required: true, message: VALIDATION_MESSAGES.NAME_REQUIRED }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="enquiry_email"
            rules={[
              { required: true, message: VALIDATION_MESSAGES.EMAIL_REQUIRED },
              { type: "email", message: VALIDATION_MESSAGES.EMAIL_INVALID },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>
          <Form.Item
            label="Mobile"
            name="enquiry_contact"
            rules={[
              { required: true, message: VALIDATION_MESSAGES.PHONE_REQUIRED },
              {
                pattern: /^[0-9]{8,15}$/,
                message: VALIDATION_MESSAGES.PHONE_INVALID,
              },
            ]}
          >
            <Input placeholder="Enter your mobile number" />
          </Form.Item>
        </Form>
      </Modal>

      <Header isHome={false}/>

      <Content className="topheaderLayout eventDetailsPage">
        <div className="eventDetailsContainer">
          <div className="eventSplitHero">
            <Card
              className="noPaddingCardBody eventHeroBannerCard eventSplitHeroMedia"
              style={{ borderRadius: 12, overflow: "hidden", padding: 0 }}
            >
              <ImageCardDisplay cardElement={event} />
            </Card>

            <div className="eventSplitHeroContent">
              <EventHeader
                className="eventHeaderInSplitHero"
                event={event}
                customizedButtons={activeRole !== "organizer" ? [
                  {
                    "text": event?.event_type_id !== 1 ? "Book Ticket" : "Enquiry",
                    "onClick": event?.event_type_id !== 1 ? handleBookEvent : openEnquiryModal,
                    "type": "primary",
                    "className": "btnGradient"
                  }
                ] : []}
              />
            </div>
          </div>

          <section className="eventMainContent">
            <div className="eventMainLeft">
              <Card bordered={false} className="eventInfoCard eventDescriptionCard">
                <Title level={4} className="eventSectionTitle">Description</Title>
                <div
                  className="eventDescriptionText eventDescriptionRich"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              </Card>
            </div>

            <aside className="eventMainRight">
              <Card bordered={false} className="eventInfoCard eventTicketSummaryCard">
                <Title level={4} className="eventSectionTitle">Tickets</Title>
                <Text className="ticket-price-range eventTicketPrice">
                  {priceRange}
                </Text>
                <Paragraph className="eventTicketHint">
                  {event?.event_type_id === 2
                    ? "Reserve your spot now. Ticket availability may be limited."
                    : "This event is open for enquiries. Share your details to connect with the organizer."}
                </Paragraph>
                {activeRole !== "organizer" && (
                  <Button
                    type="primary"
                    className="btnGradient eventTicketActionBtn"
                    onClick={event?.event_type_id !== 1 ? handleBookEvent : openEnquiryModal}
                  >
                    {event?.event_type_id !== 1 ? "Book Ticket" : "Send Enquiry"}
                  </Button>
                )}
              </Card>
            </aside>
          </section>
        </div>
      </Content>

      <Footer />
    </>
  );
}
